import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  Modal,
  FlatList,
  StyleSheet,
  ActivityIndicator,
  Alert,
} from 'react-native';
import APIService, { Zona } from '../services/api';

interface ZonaSelectorProps {
  municipioId?: number;
  selectedZona?: Zona;
  onZonaChange: (zona: Zona) => void;
  disabled?: boolean;
  style?: any;
}

const ZonaSelector: React.FC<ZonaSelectorProps> = ({
  municipioId,
  selectedZona,
  onZonaChange,
  disabled = false,
  style,
}) => {
  const [zonas, setZonas] = useState<Zona[]>([]);
  const [showZonas, setShowZonas] = useState(false);
  const [loading, setLoading] = useState(false);

  // Cargar zonas cuando cambia el municipio
  useEffect(() => {
    if (municipioId && municipioId > 0) {
      loadZonas(municipioId);
    } else {
      setZonas([]);
    }
  }, [municipioId]);

  const loadZonas = async (municipioId: number) => {
    try {
      setLoading(true);
      console.log('📍 Cargando zonas para municipio:', municipioId);
      const data = await APIService.getZonas(municipioId);
      console.log('📍 Zonas cargadas:', data.length);
      setZonas(data);
    } catch (error: any) {
      console.error('❌ Error cargando zonas:', error);
      Alert.alert(
        'Error',
        'No se pudieron cargar las zonas. Verifica tu conexión.',
        [{ text: 'OK' }]
      );
    } finally {
      setLoading(false);
    }
  };

  const handleZonaSelect = (zona: Zona) => {
    console.log('📍 Zona seleccionada:', zona);
    onZonaChange(zona);
    setShowZonas(false);
  };

  const isDisabled = disabled || !municipioId || municipioId === 0;

  return (
    <View style={[styles.container, style]}>
      <Text style={styles.label}>Zona</Text>
      
      <TouchableOpacity
        style={[
          styles.selector,
          isDisabled && styles.selectorDisabled
        ]}
        onPress={() => !isDisabled && setShowZonas(true)}
        disabled={isDisabled}
      >
        <Text style={[
          styles.selectorText,
          !selectedZona && styles.placeholderText
        ]}>
          {selectedZona ? selectedZona.nombre : 'Selecciona una zona'}
        </Text>
        {loading && <ActivityIndicator size="small" color="#007AFF" />}
      </TouchableOpacity>

      {isDisabled && !municipioId && (
        <Text style={styles.helperText}>
          Primero selecciona un municipio
        </Text>
      )}

      {/* Modal de Zonas */}
      <Modal
        visible={showZonas}
        transparent={true}
        animationType="slide"
        onRequestClose={() => setShowZonas(false)}
      >
        <View style={styles.modalContainer}>
          <View style={styles.modalContent}>
            <View style={styles.modalHeader}>
              <Text style={styles.modalTitle}>Selecciona una Zona</Text>
              <TouchableOpacity
                onPress={() => setShowZonas(false)}
                style={styles.closeButton}
              >
                <Text style={styles.closeButtonText}>✕</Text>
              </TouchableOpacity>
            </View>

            {loading ? (
              <View style={styles.loadingContainer}>
                <ActivityIndicator size="large" color="#007AFF" />
                <Text style={styles.loadingText}>Cargando zonas...</Text>
              </View>
            ) : (
              <FlatList
                data={zonas}
                keyExtractor={(item) => item.id.toString()}
                renderItem={({ item }) => (
                  <TouchableOpacity
                    style={[
                      styles.item,
                      selectedZona?.id === item.id && styles.selectedItem
                    ]}
                    onPress={() => handleZonaSelect(item)}
                  >
                    <Text style={[
                      styles.itemText,
                      selectedZona?.id === item.id && styles.selectedItemText
                    ]}>
                      {item.nombre}
                    </Text>
                  </TouchableOpacity>
                )}
                ListEmptyComponent={() => (
                  <View style={styles.emptyContainer}>
                    <Text style={styles.emptyText}>
                      No hay zonas disponibles para este municipio
                    </Text>
                  </View>
                )}
              />
            )}
          </View>
        </View>
      </Modal>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    marginBottom: 15,
  },
  label: {
    fontSize: 16,
    fontWeight: '600',
    color: '#333',
    marginBottom: 8,
  },
  selector: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    borderWidth: 1,
    borderColor: '#ddd',
    borderRadius: 8,
    padding: 15,
    backgroundColor: '#fff',
  },
  selectorDisabled: {
    backgroundColor: '#f5f5f5',
    borderColor: '#e0e0e0',
  },
  selectorText: {
    fontSize: 16,
    color: '#333',
    flex: 1,
  },
  placeholderText: {
    color: '#999',
  },
  helperText: {
    fontSize: 12,
    color: '#666',
    marginTop: 5,
    fontStyle: 'italic',
  },
  modalContainer: {
    flex: 1,
    justifyContent: 'flex-end',
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
  },
  modalContent: {
    backgroundColor: '#fff',
    borderTopLeftRadius: 20,
    borderTopRightRadius: 20,
    maxHeight: '70%',
    paddingBottom: 20,
  },
  modalHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 20,
    borderBottomWidth: 1,
    borderBottomColor: '#eee',
  },
  modalTitle: {
    fontSize: 20,
    fontWeight: '700',
    color: '#333',
  },
  closeButton: {
    padding: 5,
  },
  closeButtonText: {
    fontSize: 24,
    color: '#666',
    fontWeight: '300',
  },
  loadingContainer: {
    padding: 40,
    alignItems: 'center',
  },
  loadingText: {
    marginTop: 10,
    fontSize: 16,
    color: '#666',
  },
  item: {
    padding: 15,
    borderBottomWidth: 1,
    borderBottomColor: '#eee',
  },
  selectedItem: {
    backgroundColor: '#007AFF10',
  },
  itemText: {
    fontSize: 16,
    color: '#333',
  },
  selectedItemText: {
    color: '#007AFF',
    fontWeight: '600',
  },
  emptyContainer: {
    padding: 40,
    alignItems: 'center',
  },
  emptyText: {
    fontSize: 16,
    color: '#666',
    textAlign: 'center',
  },
});

export default ZonaSelector;
